import { hasPermission, isSuperAdmin } from 'auth/permissions';

export const paths = {
  login: '/login',
  forbidden: '/forbidden',
  notFound: '/not-found',
  overview: '/overview',
  patterns: '/patterns',
  patternCreate: '/patterns/new',
  patternEdit: (id) => `/patterns/${id}/edit`,
  weightMatrix: (id) => `/patterns/${id}/weights`,
  weights: '/weights',
  builder: '/builder',
  questionCreate: '/builder/questions/new',
  questionEdit: (id) => `/builder/questions/${id}/edit`,
  answerDetail: (id) => `/answers/${id}`,
  versions: '/versions',
  versionDetail: (id) => `/versions/${id}`,
  topThreeProfiles: '/top-three-profiles',
  topThreeProfilesWithVersion: (versionId) => `/top-three-profiles?version_id=${encodeURIComponent(versionId)}`,
  topThreeProfileDetail: (id, versionId) => (
    versionId
      ? `/top-three-profiles/${id}?version_id=${encodeURIComponent(versionId)}`
      : `/top-three-profiles/${id}`
  ),
  resultContent: '/result-content',
  advices: '/advices',
  attempts: '/attempts',
  attemptDetail: (id) => `/attempts/${id}`,
  specialists: '/specialists',
  specialistCreate: '/specialists/new',
  specialistEdit: (id) => `/specialists/${id}/edit`,
  specialistDetail: (id) => `/specialists/${id}`,
  consultations: '/consultations',
  consultationDetail: (id) => `/consultations/${id}`,
  support: '/support',
  supportTicketDetail: (id) => `/support/tickets/${id}`,
  legalDocuments: '/legal-documents',
  legalDocumentCreate: '/legal-documents/new',
  legalDocumentDetail: (id) => `/legal-documents/${id}`,
  legalDocumentEdit: (id) => `/legal-documents/${id}/edit`,
  notifications: '/notifications',
  companyPlans: '/company-plans',
  companyPlanCreate: '/company-plans/new',
  companyPlanDetail: (id) => `/company-plans/${id}`,
  companyPlanEdit: (id) => `/company-plans/${id}/edit`,
  companies: '/companies',
  companyDetail: (id) => `/companies/${id}`,
  planPurchases: '/plan-purchases',
  planPurchaseDetail: (id) => `/plan-purchases/${id}`,
  companyEntitlements: '/company-entitlements',
  companyEntitlementDetail: (id) => `/company-entitlements/${id}`,
  companyCodeBatches: '/company-code-batches',
  companyCodeBatchDetail: (id) => `/company-code-batches/${id}`,
  companyAccessCodes: '/company-access-codes',
  companyAccessCodeDetail: (id) => `/company-access-codes/${id}`,
  sponsoredAssessments: '/sponsored-assessments',
  sponsoredAssessmentResult: (attemptId) => `/sponsored-assessments/${attemptId}/result`,
};

export function firstAccessiblePath(adminOrPermissions = []) {
  const has = (slug) => hasPermission(adminOrPermissions, slug);
  if (isSuperAdmin(adminOrPermissions) || has('professional_assessment.view')) {
    return paths.overview;
  }
  if (has('professional_assessment.attempts.view')) {
    return paths.attempts;
  }
  if (has('professional_assessment.top_three_profiles.view')) {
    return paths.topThreeProfiles;
  }
  if (has('professional_assessment.result_content.view') || has('professional_assessment.tags.manage')) {
    return paths.resultContent;
  }
  if (has('professional_assessment.specialists.view') || has('professional_assessment.specialists.manage')) {
    return paths.specialists;
  }
  if (has('professional_assessment.consultations.view') || has('professional_assessment.consultations.manage')) {
    return paths.consultations;
  }
  if (has('professional_assessment.support.view') || has('professional_assessment.support.manage')) {
    return paths.support;
  }
  if (has('professional_assessment.legal_documents.view') || has('professional_assessment.legal_documents.manage')) {
    return paths.legalDocuments;
  }
  if (has('professional_assessment.notifications.view') || has('professional_assessment.notifications.manage')) {
    return paths.notifications;
  }
  if (has('professional_assessment.plans.view') || has('professional_assessment.plans.manage')) {
    return paths.companyPlans;
  }
  if (has('professional_assessment.companies.view') || has('professional_assessment.companies.manage')) {
    return paths.companies;
  }
  if (has('professional_assessment.purchases.view') || has('professional_assessment.purchases.manage')) {
    return paths.planPurchases;
  }
  if (has('professional_assessment.entitlements.view') || has('professional_assessment.entitlements.manage')) {
    return paths.companyEntitlements;
  }
  if (has('professional_assessment.codes.view') || has('professional_assessment.codes.manage')) {
    return paths.companyAccessCodes;
  }
  if (has('professional_assessment.sponsored_results.view')) {
    return paths.sponsoredAssessments;
  }
  return paths.forbidden;
}

export const homePathForPermissions = firstAccessiblePath;

export function safeReturnTo(value, fallback = paths.overview) {
  if (typeof value !== 'string') {
    return fallback;
  }
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
    return fallback;
  }
  if (value.startsWith(paths.login) || value.startsWith(paths.forbidden)) {
    return fallback;
  }
  return value;
}
